import type { BetaRawMessageStreamEvent } from '@anthropic-ai/sdk/resources/beta/messages/messages'

// Reads the coaching letter's stream: Anthropic's server-sent events, which the server
// forwards untouched (server/providers.ts). Type-only SDK import: no SDK code ships to the app.

type StreamEvent = BetaRawMessageStreamEvent | { type: 'ping' } | { type: 'error'; error: { message?: string } }

export interface StreamHandlers {
  onThinking?: (delta: string) => void
  onText?: (delta: string) => void
  /** The requested model declined and `model` starts the letter over. */
  onFallback?: (model: string) => void
}

export interface StreamResult {
  stopReason: string | null
  /** The model the API reports, which may carry a date suffix (claude-haiku-4-5-20251001). */
  model: string | null
  /** Set when the requested model declined and this one wrote the letter instead. */
  fallbackModel: string | null
  inputTokens: number | null
  outputTokens: number | null
}

function handle(event: StreamEvent, handlers: StreamHandlers, result: StreamResult) {
  switch (event.type) {
    case 'message_start':
      result.model = event.message.model
      result.inputTokens = event.message.usage.input_tokens
      break
    case 'content_block_start':
      if (event.content_block.type === 'fallback') {
        result.fallbackModel = event.content_block.to.model
        handlers.onFallback?.(event.content_block.to.model)
      }
      break
    case 'content_block_delta':
      if (event.delta.type === 'thinking_delta') handlers.onThinking?.(event.delta.thinking)
      else if (event.delta.type === 'text_delta') handlers.onText?.(event.delta.text)
      break
    case 'message_delta':
      result.stopReason = event.delta.stop_reason ?? result.stopReason
      if (typeof event.usage.output_tokens === 'number') result.outputTokens = event.usage.output_tokens
      if (typeof event.usage.input_tokens === 'number') result.inputTokens = event.usage.input_tokens
      break
    case 'error':
      throw new Error(event.error.message ?? 'The letter stream failed. Try again.')
  }
}

/** Reads the whole stream, calling handlers as thinking and text arrive. */
export async function readLetterStream(
  body: ReadableStream<Uint8Array>,
  handlers: StreamHandlers = {},
): Promise<StreamResult> {
  const result: StreamResult = { stopReason: null, model: null, fallbackModel: null, inputTokens: null, outputTokens: null }
  const reader = body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''

  const flush = (block: string) => {
    const data = block
      .split(/\r?\n/)
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n')
    if (data) handle(JSON.parse(data) as StreamEvent, handlers, result)
  }

  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += value
    // Events end with a blank line; keep a partial event for the next chunk.
    const blocks = buffer.split(/\r?\n\r?\n/)
    buffer = blocks.pop() ?? ''
    blocks.forEach(flush)
  }
  if (buffer.trim()) flush(buffer)
  return result
}
